import { createApp } from 'vue';

import axios from 'axios';
// Global Components
import hljsVuePlugin from '@highlightjs/vue-plugin';

import Multiselect from 'vue-multiselect';
import { plugin, defaultConfig } from '@formkit/vue';
import WootSwitch from 'components/ui/Switch.vue';
import WootWizard from 'components/ui/Wizard.vue';
import FloatingVue from 'floating-vue';
import WootUiKit from 'dashboard/components';
import App from 'dashboard/App.vue';
import i18n from 'dashboard/i18n';
import createAxios from 'dashboard/helper/APIHelper';

import commonHelpers, { isJSONValid } from 'dashboard/helper/commons';
import { sync } from 'vuex-router-sync';
import router, { initalizeRouter } from 'dashboard/routes';
import store from 'dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from 'dashboard/helper/scriptHelpers.js';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';

import { vResizeObserver } from '@vueuse/components';
import { directive as onClickaway } from 'vue3-click-away';

import 'floating-vue/dist/style.css';

sync(store, router);

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(router);

if (window.errorLoggingConfig) {
  Sentry.init({
    app,
    release: window.versionHash,
    dsn: window.errorLoggingConfig,
    // Envelopes go through our own server (see SentryTunnelController) because ad blockers
    // block requests to *.ingest.sentry.io. Keep in sync with SENTRY_TUNNEL_PATH.
    tunnel: '/jb/e',
    // Replay envelopes are Uint8Array bodies, which fetch would send with no Content-Type at
    // all; Rack then treats them as form data and Rails rejects them with a 400.
    transportOptions: {
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
    },
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    sendDefaultPii: true,
    enableLogs: true,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        networkDetailAllowUrls: ['chat.jarabot.com'],
        networkResponseHeaders: ['X-Request-Id'],
      }),
      Sentry.browserTracingIntegration({ router }),
    ],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      /^TSS:/,
      /Network Error/,
    ],
  });
}

app.use(VueDOMPurifyHTML, domPurifyConfig);
app.use(WootUiKit);
app.use(
  plugin,
  defaultConfig({
    rules: {
      JSON: ({ value }) => isJSONValid(value),
    },
  })
);
app.use(FloatingVue, {
  instantMove: true,
  arrowOverflow: false,
  disposeTimeout: 500,
  boundary: 'viewport',
  overflowPadding: 8,
  // Hidden tooltip poppers are taken out of layout with `display: none`
  // (see App.vue) so an undisposed/leaked popper can't take up space and shift
  // surrounding content. The side effect is that the popper measures 0×0 while
  // hidden, so floating-ui's first position calc on show places a zero-size box
  // at the trigger anchor — the tooltip then renders on top of / under the
  // trigger, and the zero-size box also never trips the viewport overflow check
  // so it can clip at a screen edge. The tooltip theme defaults to
  // `handleResize: false`, which means nothing re-runs the calc once the popper
  // gets its real size. Enabling it makes floating-ui observe the 0→real resize
  // and re-settle the tooltip beside the trigger and inside the viewport.
  themes: {
    tooltip: {
      handleResize: true,
    },
  },
});
app.use(hljsVuePlugin);

app.component('multiselect', Multiselect);
app.component('woot-switch', WootSwitch);
app.component('woot-wizard', WootWizard);
app.component('fluent-icon', FluentIcon);

app.directive('resize', vResizeObserver);
app.directive('on-clickaway', onClickaway);

// load common helpers into js
commonHelpers();
window.WootConstants = constants;
window.axios = createAxios(axios);
// [VITE] Disabled this we don't need it, we can use `useEmitter` directly
// app.prototype.$emitter = emitter;

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();

window.onload = () => {
  app.mount('#app');
};
